import React, { useState, useEffect } from 'react';
import { Box, VStack, Heading, Text, Button, Input, InputGroup, InputRightElement, useToast, Spinner } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';
import { supabase } from '../../services/supabaseClient';

// Função para obter parâmetros da URL
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const AdminMobileLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyingToken, setVerifyingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const query = useQuery();
  const token = query.get('token');

  // Verificar se o token é válido
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifyingToken(false);
        setTokenValid(false);
        return;
      }

      try {
        // Verificar se o token existe e não expirou
        const now = new Date().toISOString();
        const { data, error } = await supabase
          .from('admin_login_tokens')
          .select('*')
          .eq('token', token)
          .eq('used', false)
          .gt('expires_at', now)
          .single();

        if (error || !data) {
          toast({
            title: 'Token inválido ou expirado',
            description: 'Por favor, escaneie o QR Code novamente.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          setTokenValid(false);
        } else {
          setTokenValid(true);
        }
      
      } catch (error) {
        console.error('Erro ao verificar token:', error);
        setTokenValid(false);
      } finally {
        setVerifyingToken(false);
      }
    };

    verifyToken();
  }, [token, toast]);

  const handleLogin = async () => {
    if (!email || !password) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Por favor, preencha todos os campos.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      // Verificar credenciais de administrador no Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data && data.user) {
        // Temporariamente considerando qualquer usuário autenticado como administrador
        // Futuramente, podemos criar uma tabela admin_users para controle mais preciso
        const adminData = {
          nome: data.user.user_metadata?.name || data.user.email,
          user_id: data.user.id
        };

        // Marcar o token como usado
        await supabase
          .from('admin_login_tokens')
          .update({ used: true, used_at: new Date().toISOString() })
          .eq('token', token);

        // Enviar evento para o totem fazer login automático
        await supabase
          .channel('admin-login')
          .send({
            type: 'broadcast',
            event: 'admin-login',
            payload: { 
              token,
              userId: data.user.id,
              userName: adminData.nome || data.user.email,
              userEmail: data.user.email
            }
          });

        toast({
          title: 'Login realizado com sucesso',
          description: 'Você pode fechar esta janela e voltar ao totem.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Redirecionar para uma página de sucesso
        setTimeout(() => {
          navigate('/mobile/admin-success');
        }, 2000);
      }
    } catch (error) {
      toast({
        title: 'Erro no login',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Renderizar página de carregamento
  if (verifyingToken) {
    return (
      <Box
        minH="100vh"
        width="100%"
        bg="linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(26,32,44,0.95) 100%)"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        padding={8}
      >
        <Spinner size="xl" color="yellow.400" thickness="4px" />
        <Text color="white" mt={4}>Verificando token...</Text>
      </Box>
    );
  }

  // Renderizar página de erro se o token for inválido
  if (!tokenValid) {
    return (
      <Box
        minH="100vh"
        width="100%"
        bg="linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(26,32,44,0.95) 100%)"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        padding={8}
      >
        <Box 
          width="100%" 
          maxWidth="400px" 
          bg="gray.900" 
          p={8} 
          borderRadius="xl" 
          boxShadow="0 4px 30px rgba(0, 0, 0, 0.4)"
          border="1px solid"
          borderColor="red.500"
        >
          <VStack spacing={6} align="stretch">
            <Heading color="red.400" textAlign="center" size="lg">
              Token Inválido ou Expirado
            </Heading>
            <Text color="gray.300" textAlign="center">
              O QR Code que você escaneou é inválido ou expirou. Por favor, escaneie um novo QR Code no totem.
            </Text>
            <Button colorScheme="red" onClick={() => window.close()}>
              Fechar
            </Button>
          </VStack>
        </Box>
      </Box>
    );
  }

  // Renderizar página de login
  return (
    <Box
      minH="100vh"
      width="100%"
      bg="linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(26,32,44,0.95) 100%)"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      padding={8}
    >
      <Box 
        width="100%" 
        maxWidth="400px" 
        bg="gray.900" 
        p={8} 
        borderRadius="xl" 
        boxShadow="0 4px 30px rgba(0, 0, 0, 0.4)"
        border="1px solid"
        borderColor="yellow.500"
      >
        <VStack spacing={6} align="stretch">
          <Heading 
            color="yellow.400" 
            textAlign="center" 
            size="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <FaLock style={{ marginRight: '10px' }} /> Login Administrativo
          </Heading>
          
          <Text color="gray.300" textAlign="center">
            Faça login com suas credenciais de administrador para acessar o totem
          </Text>
          
          <Input
            placeholder="E-mail"
            size="lg"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            bg="whiteAlpha.100"
            border="1px solid"
            borderColor="gray.600"
            color="white"
            _placeholder={{ color: 'whiteAlpha.500' }}
            _focus={{ borderColor: 'yellow.300', boxShadow: '0 0 0 1px rgba(255, 215, 0, 0.6)' }}
          />
          
          <InputGroup size="lg">
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="gray.600"
              color="white"
              _placeholder={{ color: 'whiteAlpha.500' }}
              _focus={{ borderColor: 'yellow.300', boxShadow: '0 0 0 1px rgba(255, 215, 0, 0.6)' }}
            />
            <InputRightElement>
              <Button 
                h="1.75rem" 
                size="sm" 
                onClick={() => setShowPassword(!showPassword)}
                bg="transparent"
                _hover={{ bg: 'whiteAlpha.200' }}
              >
                {showPassword ? <FaEyeSlash color="gray" /> : <FaEye color="gray" />}
              </Button>
            </InputRightElement>
          </InputGroup>
          
          <Button
            colorScheme="yellow"
            size="lg"
            onClick={handleLogin}
            isLoading={loading}
            loadingText="Autenticando..."
          >
            Entrar
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default AdminMobileLoginPage;
